/**
 * Rueckweg und Orientierung (Punkt 115, Paket 2; Antoines Entscheidungen G2/G3/G4 vom 24.09.2026).
 *
 * G2 „Zurück“ = BEIDES: oben steht immer ein fester Breadcrumb (Vorgang: Anfragen oder Buchungen
 * je nach Phase), zusaetzlich „← Zurück zu <Herkunft>“, wenn man von einer anderen Seite kam. Die
 * Herkunft steht in der Adresse (`?von=<pfad>&vonName=<anzeige>`), damit sie Neuladen und neue Tabs
 * uebersteht. Dieselben Parameternamen nutzt Paket 1 (`zahlungenPfad` in `lib/pfade.ts`), beide
 * Zweige passen also zusammen.
 */
import { useEffect, useSyncExternalStore } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { ANFRAGEN_PFAD, BUCHUNGEN_PFAD, sichereHerkunft } from "./pfade";

/** Haengt die Herkunft an ein Ziel an (bestehende Query bleibt erhalten). */
export function mitHerkunft(ziel: string, von: string, vonName?: string | null): string {
  const [pfad, query = ""] = ziel.split("?", 2);
  const p = new URLSearchParams(query);
  p.set("von", von);
  if (vonName) p.set("vonName", vonName);
  else p.delete("vonName");
  return `${pfad}?${p.toString()}`;
}

/** Die Herkunft der aktuellen Seite aus `?von=`/`?vonName=`, oder null. */
export function useHerkunft(): { to: string; name: string | null } | null {
  const [params] = useSearchParams();
  const to = sichereHerkunft(params.get("von")); // die EINE Pruefung, lib/pfade.ts
  return to ? { to, name: params.get("vonName") } : null;
}

/**
 * Die aktuelle Adresse als Herkunft fuer einen weiterfuehrenden Link — ohne eine eigene, schon
 * mitgebrachte Herkunft (sonst wuerden Ketten wie „Kunde → Vorgang → Kunde“ in der Adresse wachsen).
 */
export function herkunftVon(pathname: string, search: string): string {
  const p = new URLSearchParams(search);
  p.delete("von");
  p.delete("vonName");
  const q = p.toString();
  return q ? `${pathname}?${q}` : pathname;
}

/**
 * Fuer Links, die von HIER auf einen Vorgang/Kunden fuehren: `mitHier(ziel, "Debora Ribeiro")`
 * haengt die aktuelle Adresse als Herkunft an, damit die Zielseite „← Zurück zu …“ zeigt.
 */
export function useMitHerkunft(): (ziel: string, vonName?: string | null) => string {
  const { pathname, search } = useLocation();
  return (ziel, vonName) => mitHerkunft(ziel, herkunftVon(pathname, search), vonName);
}

/**
 * F2 (B0 „L7“): Buchung = gebundener Status (spiegelt `apps/api/src/lib/event-status.ts`) oder
 * `cancelled` NACH einer Zusage (`wonAt`). Alles andere ist (noch) eine Anfrage.
 */
const BUCHUNG_STATUS = new Set(["offer_chosen", "paid", "payment_failed", "balance_due", "completed"]);
export function istBuchung(e: { status: string; wonAt?: string | null }): boolean {
  return BUCHUNG_STATUS.has(e.status) || (e.status === "cancelled" && e.wonAt != null);
}

/** Die Liste, in der ein Vorgang steht — Ziel des Breadcrumbs und Markierung der Seitenleiste. */
export function vorgangsListe(e: { status: string; wonAt?: string | null }): { label: string; to: string } {
  return istBuchung(e) ? { label: "Buchungen", to: BUCHUNGEN_PFAD } : { label: "Anfragen", to: ANFRAGEN_PFAD };
}

/**
 * G4: Die Vorgangsseite meldet der Shell, welcher Bereich markiert wird (`bereich` = Listen-Pfad)
 * und was oben als Titel steht (der Name des Vorgangs statt „MAESTRO“). Ein winziger Speicher statt
 * eines Contexts: die Seite liegt tief unter `AppShell`, und es gibt genau einen Schreiber.
 */
export type SeitenKontext = { bereich: string; titel: string } | null;
let seitenKontext: SeitenKontext = null;
const hoerer = new Set<() => void>();
function setzeSeitenKontext(k: SeitenKontext) {
  seitenKontext = k;
  hoerer.forEach((h) => h());
}

export function useSeitenKontext(): SeitenKontext {
  return useSyncExternalStore(
    (h) => {
      hoerer.add(h);
      return () => hoerer.delete(h);
    },
    () => seitenKontext,
    () => seitenKontext,
  );
}

/** Setzt den Kontext, solange die aufrufende Seite steht (null = nichts melden). */
export function useSeitenKontextMelden(bereich: string | null, titel: string | null) {
  useEffect(() => {
    if (!bereich || !titel) return;
    setzeSeitenKontext({ bereich, titel });
    return () => setzeSeitenKontext(null);
  }, [bereich, titel]);
}
